import { CheckCircle2, XCircle, Loader2, SkipForward } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { ApiWorkflowRun, ApiWorkflowStepLog } from '@/api/workflows';

const statusIcon: Record<string, typeof CheckCircle2> = {
  completed: CheckCircle2,
  failed: XCircle,
  running: Loader2,
  skipped: SkipForward,
  pending: Loader2,
};

const statusVariant: Record<string, string> = {
  completed: 'bg-green-500/15 text-green-700 dark:text-green-400',
  failed: 'bg-destructive/15 text-destructive',
  skipped: 'bg-muted text-muted-foreground',
  running: 'bg-primary/15 text-primary',
  pending: 'bg-muted text-muted-foreground',
};

export interface WorkflowTestResultsProps {
  run: ApiWorkflowRun | null;
  open: boolean;
  onClose: () => void;
}

function formatSnapshot(snapshot: string): string {
  try {
    return JSON.stringify(JSON.parse(snapshot), null, 2);
  } catch {
    return snapshot;
  }
}

function StepRow({ step }: { step: ApiWorkflowStepLog }) {
  const Icon = statusIcon[step.status] ?? Loader2;
  const variant = statusVariant[step.status] ?? 'bg-muted';

  return (
    <div className="rounded-lg border bg-card p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <Icon className={cn('h-4 w-4 shrink-0', step.status === 'running' && 'animate-spin')} />
          <span className="truncate font-medium">{step.step_label}</span>
          <Badge variant="secondary" className={cn('shrink-0 text-xs', variant)}>
            {step.status}
          </Badge>
        </div>
        {step.duration_ms != null && (
          <span className="text-xs text-muted-foreground">{step.duration_ms}ms</span>
        )}
      </div>
      {step.message && <p className="mt-2 text-xs text-muted-foreground">{step.message}</p>}
      {step.output_snapshot && (
        <pre className="mt-2 max-h-72 overflow-auto rounded bg-muted/50 p-2 text-xs leading-relaxed">
          {formatSnapshot(step.output_snapshot)}
        </pre>
      )}
    </div>
  );
}

export function WorkflowTestResults({ run, open, onClose }: WorkflowTestResultsProps) {
  const steps = run?.step_logs ?? [];
  const runStatus = run?.status ?? 'pending';

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-2xl">
        <DialogHeader>
          <DialogTitle>Test run results</DialogTitle>
        </DialogHeader>
        {run && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Badge
                className={cn(
                  'text-xs',
                  runStatus === 'completed' && statusVariant.completed,
                  runStatus === 'failed' && statusVariant.failed,
                  runStatus === 'running' && statusVariant.running
                )}
              >
                {runStatus}
              </Badge>
              {run.is_test_run && (
                <Badge variant="outline" className="text-xs">
                  Test run
                </Badge>
              )}
              {run.error_message && (
                <span className="text-sm text-destructive">{run.error_message}</span>
              )}
            </div>
            {run.summary && <p className="text-sm text-muted-foreground">{run.summary}</p>}
            <div>
              <h4 className="mb-2 text-sm font-medium">Steps</h4>
              <ScrollArea className="h-[280px] pr-4">
                <div className="space-y-2">
                  {steps.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No step logs.</p>
                  ) : (
                    steps.map((step) => <StepRow key={step.id} step={step} />)
                  )}
                </div>
              </ScrollArea>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
